import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { FormHelperText } from '@mui/material'

import { CustomFields } from './CustomFields'

import type { CustomFieldValue, ExecutionEventsFormValues } from '../../../typings'

export type CustomFieldsFieldListValue = ExecutionEventsFormValues['customFields']
const defaultComponentValue: CustomFieldsFieldListValue = {}

export type CustomFieldsFieldListProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = CustomFieldsFieldListValue,
> = { testId: string } & TidalFormBridgeOptions<
  CustomFieldsFieldListValue,
  TargetField,
  State,
  AcceptedFormValue
>

export function CustomFieldsField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = CustomFieldsFieldListValue,
>(props: CustomFieldsFieldListProps<TargetField, State, AcceptedFormValue>) {
  const {
    testId,
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: {
      value,
      onChange,
      fieldDisabled,
      errorOrHelperText,
      showErrorAccent,
      fieldHasError,
    },
  } = useTidalFormBridge<CustomFieldsFieldListValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const handleOnChange = (key: string, customFieldValue: string) => {
    onChange({ ...value, [key]: { ...value?.[key], value: customFieldValue } as CustomFieldValue })
  }

  return (
    <>
      <CustomFields
        onChange={handleOnChange}
        value={value}
        testId={testId}
        fieldDisabled={fieldDisabled}
      />
      {showErrorAccent && fieldHasError && (
        <FormHelperText error>{errorOrHelperText}</FormHelperText>
      )}
    </>
  )
}
